import escapeHtml from 'escape-html'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { selectCockpitUserAccess } from '../../../../model/selectors'
import {
  CockpitTableColumnTitle,
  CockpitTableRowTitle,
} from '../../../components/cockpitTableTitle'

import { REPORT_ACCESS_ISSUE_HREF } from './constants'

import styles from './HelpPanelAccess.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.greeting
const PANEL_KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.helpPanel
const roleKeys: Record<string, string> = KEYS.roles

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('')

/** Whose cockpit this is: the user, the role they view it as, and the geography, routes to market
 *  and offerings that role gives access to, with the way to report access that looks wrong. */
export const HelpPanelAccess = () => {
  const access = useSelector(selectCockpitUserAccess)
  const t = useSafetyTranslation()
  const name = access.name || '—'
  const initials = getInitials(access.name ?? '')
  const role =
    access.accessRole || (roleKeys[access.role] ? t(roleKeys[access.role]) : access.role) || '—'
  const accessLevel = access.accessLevel || '—'
  const accessLevelDetails =
    accessLevel.trim().toLowerCase() === 'worldwide' ? '' : access.accessLevelDetails
  const facts = [
    { key: KEYS.role, value: role },
    {
      key: KEYS.accessLevel,
      value: accessLevelDetails ? `${accessLevel}: ${accessLevelDetails}` : accessLevel,
      wide: true,
    },
    { key: KEYS.rtm, value: access.accessRtm || '—' },
    { key: KEYS.offerings, value: access.accessOfferings || '—' },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.who}>
        {initials && (
          <span className={styles.avatar} aria-hidden="true">
            {initials}
          </span>
        )}
        <div className={styles.identity}>
          <span className={styles.name}>{name}</span>
          <span className={styles.role}>
            <Translation
              span
              text={PANEL_KEYS.viewingAs}
              replaceParams={[{ key: '{role}', value: escapeHtml(role) }]}
            />
          </span>
        </div>
      </div>

      <dl className={styles.facts}>
        {facts.map(({ key, value, wide }) => (
          <div key={key} className={wide ? styles.wide : undefined}>
            <dt>
              <CockpitTableColumnTitle className={styles.label}>
                <Translation span text={key} />
              </CockpitTableColumnTitle>
            </dt>
            <dd>
              <CockpitTableRowTitle className={styles.value}>{value}</CockpitTableRowTitle>
            </dd>
          </div>
        ))}
      </dl>

      <div className={styles.footer}>
        <span className={styles.note}>
          <Translation span text={PANEL_KEYS.accessNote} />
        </span>
        <Button size="sm" href={REPORT_ACCESS_ISSUE_HREF}>
          <Translation span text={KEYS.reportIssue} />
        </Button>
      </div>
    </div>
  )
}

HelpPanelAccess.displayName = 'HelpPanelAccess'
